// src/components/discover/public-character-generator.tsx
"use client";

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import Image from 'next/image';
import { useAuth } from '@/context/auth-context';
import { useI18n } from '@/context/i18n-context';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Sparkles, Loader2, ArrowRight, RefreshCw, AlertTriangle, UploadCloud, Edit3, MessageSquare, Image as ImageIconLucide } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Character } from '@/lib/types';
import { generatePublicCharacter } from '@/ai/flows/public-character-flow';
import { generatePublicCharacterFromImage } from '@/ai/flows/generate-public-character-from-image-flow';
import { Skeleton } from '@/components/ui/skeleton';
import Link from 'next/link';

const IMAGE_UPLOAD_LIMIT_MB = 4;

type GenerationStage = 'idle' | 'thinking' | 'generatingCharacter' | 'generatingImage' | 'completed' | 'error';
type CreationMode = 'text' | 'image';

export default function PublicCharacterGenerator() {
  const { user, openAuthModal } = useAuth();
  const { t } = useI18n();
  const router = useRouter();
  
  const [prompt, setPrompt] = useState('');
  const [stage, setStage] = useState<GenerationStage>('idle');
  const [generatedCharacter, setGeneratedCharacter] = useState<Character | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<CreationMode>('text');

  // State for image upload
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);


  const stageMessages: Record<Exclude<GenerationStage, 'idle' | 'completed' | 'error'>, string> = {
    thinking: t('thinking') || "Thinking...",
    generatingCharacter: t('generatingCharacter') || "Generating Character...",
    generatingImage: t('generatingImage') || "Generating Image...",
  };
  
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > IMAGE_UPLOAD_LIMIT_MB * 1024 * 1024) {
        setImageError(`Image size exceeds ${IMAGE_UPLOAD_LIMIT_MB}MB.`);
        return;
      }
      setImageError(null);
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };


  const handleCreate = async () => {
    if (stage !== 'idle') return;
    
    setError(null);
    setGeneratedCharacter(null);
    setStage('thinking');

    try {
        let result;
        if (mode === 'image' && imageFile) {
            const reader = new FileReader();
            reader.readAsDataURL(imageFile);
            reader.onload = async () => {
                try {
                    const dataUri = reader.result as string;
                    const imageResult = await generatePublicCharacterFromImage({
                        photoDataUri: dataUri,
                        userId: user?.uid,
                        creatorHandle: user?.displayName || undefined,
                    });
                    setGeneratedCharacter(imageResult.character);
                    setStage('completed');
                } catch(e: any) {
                    console.error("Public character generation from image failed:", e);
                    setError(e.message || "An unknown error occurred during image processing.");
                    setStage('error');
                }
            };
            reader.onerror = () => {
                setError("Could not read the uploaded image file.");
                setStage('error');
            };
            // The result setting will be handled by the onload/onerror handlers
            return;
        } else if (mode === 'text' && prompt.trim()) {
             result = await generatePublicCharacter({
                prompt,
                userId: user?.uid,
                creatorHandle: user?.displayName || undefined
            });
        } else {
            setError("No prompt or image provided.");
            setStage('idle');
            return;
        }

        if (result && result.character) {
            setGeneratedCharacter(result.character);
            setStage('completed');
        } else {
            throw new Error("Character generation returned no data.");
        }
    } catch (e: any) {
        console.error("Public character generation failed:", e);
        setError(e.message || "An unknown error occurred during character generation.");
        setStage('error');
    }
  };

  const handleReset = () => {
    setPrompt('');
    setImageFile(null);
    setImagePreview(null);
    setImageError(null);
    setStage('idle');
    setGeneratedCharacter(null);
    setError(null);
    // Don't reset mode, let the user choose
  };

  const handleStartChat = () => {
    if (!generatedCharacter) return;
    const chatUrl = `/chat/${generatedCharacter.username || generatedCharacter.id}`;
    
    if (user) {
        router.push(chatUrl);
    } else {
        openAuthModal('login', chatUrl);
    }
  };

  const handleEditClick = () => {
      if (!generatedCharacter) return;
      const editUrl = `/create-character?edit=${generatedCharacter.id}`;
      if (user) {
          router.push(editUrl);
      } else {
          openAuthModal('login', editUrl);
      }
  };
  
  const renderErrorState = (errorMessage: string | null) => (
    <div className="flex flex-col items-start justify-center w-full text-left">
      <Alert variant="destructive" className="bg-destructive/20 border-destructive text-destructive-foreground w-full">
        <AlertTriangle className="h-4 w-4"/>
        <AlertTitle>{t('generationError') || "Generation Error"}</AlertTitle>
        <AlertDescription className="text-xs">{errorMessage || "An unknown error occurred."}</AlertDescription>
      </Alert>
      <div className="mt-4 flex w-full">
          <Button onClick={handleReset} variant="outline" size="lg" className="bg-transparent border-white/20 text-white hover:bg-white/10 hover:text-white rounded-full h-12 px-6">
              <RefreshCw className="mr-2 h-4 w-4"/>
              {t('tryAgain')}
          </Button>
      </div>
    </div>
  );
  
  const renderIdleContent = () => (
    <div className="flex flex-col md:flex-row items-center justify-start gap-8 w-full">
      <div className="flex-grow flex flex-col justify-center gap-3 w-full max-w-2xl md:text-left text-center">
        <h2
          className="text-3xl font-bold"
          style={{
            background: 'linear-gradient(to right, #c084fc 0%, #ec4899 50%, #fb923c 100%)',
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            color: 'transparent',
          }}
        >
          {t('createAndChat')}
        </h2>
        {mode === 'text' ? (
          <>
            <p className="text-white/80 hidden md:block">Describe a character to create and chat with instantly.</p>
             <div className="relative w-full h-16 p-0.5 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400 rounded-full mt-2 shadow-lg">
                <div className="flex items-center w-full h-full bg-slate-900 rounded-full">
                    <Input
                        type="text"
                        placeholder={t('generateCharacterPlaceholder') || "Describe a character to create & chat with..."}
                        value={prompt}
                        onChange={(e) => setPrompt(e.target.value)}
                        className="h-full w-full text-base bg-transparent border-none text-white placeholder:text-white/60 focus:ring-0 pl-6 pr-28"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleCreate();
                          }
                        }}
                    />
                    <Button
                        onClick={handleCreate}
                        disabled={!prompt.trim() || stage !== 'idle'}
                        className="absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-primary hover:bg-primary/90 flex-shrink-0"
                    >
                        <Sparkles className="h-5 w-5" />
                        <span className="sr-only">{t('create')}</span>
                    </Button>
                </div>
            </div>
            <div className="flex items-center justify-center md:justify-start">
              <Button variant="ghost" onClick={() => setMode('image')} className="text-white/70 hover:text-white self-start flex items-center px-0">
                  <span className="flex items-center gap-2">
                    <ImageIconLucide size={16} /> Generate from image
                  </span>
                  <ArrowRight size={16} className="ml-2" />
              </Button>
            </div>
          </>
        ) : (
           <>
              <p className="text-white/80 hidden md:block">Upload an image to generate a character from it.</p>
              <div className="flex items-center w-full mt-2 gap-2">
                 <label htmlFor="image-upload" className="flex flex-col items-center justify-center w-full h-24 border-2 border-slate-600 border-dashed rounded-xl cursor-pointer bg-slate-700/20 hover:bg-slate-700/40">
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        {imagePreview ? (
                            <Image src={imagePreview} alt="Preview" width={64} height={64} className="object-contain rounded-md h-16" />
                        ) : (
                            <>
                                <UploadCloud className="w-8 h-8 mb-2 text-slate-400" />
                                <p className="text-xs text-slate-400"><span className="font-semibold">Click to upload</span> or drag &amp; drop</p>
                            </>
                        )}
                    </div>
                    <Input id="image-upload" type="file" className="hidden" accept="image/png, image/jpeg, image/webp" onChange={handleFileChange} />
                </label>
                 <Button
                    onClick={handleCreate}
                    disabled={!imageFile || stage !== 'idle'}
                    className="h-24 px-5 bg-primary hover:bg-primary/90 rounded-xl flex-shrink-0"
                  >
                    <Sparkles className="mr-2 h-4 w-4" />
                    <span className="hidden sm:inline">{t('createAndChat')}</span>
                    <span className="sm:hidden">{t('create')}</span>
                  </Button>
              </div>
              {imageError && <p className="text-sm text-destructive mt-1">{imageError}</p>}
              <div className="flex items-center justify-center md:justify-start">
                <Button variant="ghost" onClick={() => setMode('text')} className="text-white/70 hover:text-white self-start flex items-center px-0">
                  <span className="flex items-center gap-2">
                    <MessageSquare size={16}/> Generate from text
                  </span>
                  <ArrowRight size={16} className="ml-2"/>
                </Button>
              </div>
          </>
        )}
      </div>
       <div className="h-80 w-full max-w-[500px] hidden md:block">
            <Image
                src="/home1.png"
                alt="Character illustration"
                width={500}
                height={500}
                className="object-contain w-full h-full"
                data-ai-hint="character illustration"
                priority
            />
        </div>
    </div>
  );
  
  const renderLoadingContent = () => (
     <div className="flex flex-col md:flex-row items-center justify-center gap-8 w-full">
        <div className="relative w-full aspect-[3/4] max-w-[192px] rounded-2xl flex-shrink-0">
          <Skeleton className="absolute inset-0 bg-white/10" />
        </div>
        <div className="flex-grow flex flex-col justify-center text-center md:text-left max-w-md w-full">
             <p className="text-sm text-primary font-semibold">{stageMessages[stage as keyof typeof stageMessages]}</p>
             <h3 className="text-3xl font-bold text-white mt-1 break-words">"{prompt || 'Uploaded Image'}"</h3>
             <div className="flex flex-wrap gap-2 mt-3 justify-center md:justify-start">
                <Skeleton className="h-5 w-16 rounded-full bg-white/10" />
                <Skeleton className="h-5 w-20 rounded-full bg-white/10" />
                <Skeleton className="h-5 w-14 rounded-full bg-white/10" />
             </div>
             <div className="flex flex-col sm:flex-row gap-3 mt-5 justify-center md:justify-start">
                <Skeleton className="h-12 px-5 rounded-full bg-white/10 w-40" />
                <Skeleton className="h-12 px-5 rounded-full bg-white/10 w-40" />
             </div>
        </div>
    </div>
  );

  const renderCompletedContent = () => {
     if (!generatedCharacter) return renderErrorState("Character data is missing.");
     return (
        <div className="flex flex-col md:flex-row items-center justify-center gap-8 w-full">
            <div className="relative aspect-[3/4] w-48 rounded-2xl overflow-hidden shadow-2xl flex-shrink-0 border-2 border-primary/50 group">
               <Image
                  src={generatedCharacter.imageUrl}
                  alt={generatedCharacter.name}
                  fill
                  className="object-cover group-hover:scale-105 transition-transform duration-300"
                  data-ai-hint={generatedCharacter.imageHint || 'character portrait'}
               />
               <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
            </div>
            <div className="flex-grow flex flex-col justify-center text-center md:text-left max-w-md w-full">
              <p className="text-sm text-primary font-semibold">{t('characterCreated')}</p>
              <h3 className="text-3xl font-bold text-white mt-1">{generatedCharacter.name}</h3>
              <p className="text-sm text-white/80 mt-2 line-clamp-3">{generatedCharacter.description}</p>
              <div className="flex flex-wrap gap-2 mt-3 justify-center md:justify-start">
                {generatedCharacter.tags.map(tag => (
                  <Badge key={tag} variant="secondary" className="bg-white/10 text-white/90 border-transparent">{tag}</Badge>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row gap-3 mt-5 justify-center md:justify-start">
                  <Button onClick={handleStartChat} size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full h-12 px-6">
                    <ArrowRight className="mr-2 h-4 w-4"/>
                    {t('startChat')}
                  </Button>
                  <Button onClick={handleEditClick} size="lg" variant="outline" className="bg-transparent border-white/20 text-white hover:bg-white/10 hover:text-white rounded-full h-12 px-6 w-full sm:w-auto">
                    <Edit3 className="mr-2 h-4 w-4"/>
                    Edit
                  </Button>
              </div>
               {!user && (
                  <p className="text-xs text-white/70 mt-4 text-center md:text-left">
                    <button onClick={handleStartChat} className="font-semibold underline hover:text-white">
                        {t('loginToSave')}
                    </button> {t('toSaveCharacter')}
                  </p>
                )}
            </div>
        </div>
     );
  }

  return (
    <div className="w-full max-w-6xl">
      <div className="flex items-center justify-center gap-x-8 gap-y-6 animate-in fade-in-50 duration-500 py-6 min-h-[350px]">
        {stage === 'idle' && renderIdleContent()}
        {(stage === 'thinking' || stage === 'generatingCharacter' || stage === 'generatingImage') && renderLoadingContent()}
        {stage === 'completed' && renderCompletedContent()}
        {stage === 'error' && (
          <div className="w-full max-w-xl">
            {renderErrorState(error)}
          </div>
        )}
      </div>
    </div>
  );
}
